// ============================================================================
// CLEO'S SECRET — Markdown render
// File: src/components/program/MarkdownRender.jsx
//
// DIY markdown-to-JSX renderer. Used by WeeklyModule for the_science and
// the_protocol fields, which Robyn authors as markdown via the admin CMS.
//
// Support set (V1):
//   ## Heading        → <h3> italic BR2 22-26px
//   **bold**          → <strong> INK 700
//   *italic* / _it_   → <em> italic BR2
//   - or * list       → <ul>/<li> with 9px diamond bullets
//   blank-line block  → <p> body prose
//   bare https:// URL → <a target="_blank" rel="noopener noreferrer">
//
// Not supported: tables, code blocks, images, nested lists, link text
// ([text](url)). Robyn authors content within this set; the admin editor
// hint copy explains it.
//
// HTML escaping at parse time — content is GP-authored, not patient-supplied,
// but defensive escaping costs nothing.
// ============================================================================

import React from 'react';
import { P, FF } from '../../lib/design-tokens';

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Inline parsers: walk a line, emit array of strings + JSX tokens.
// Order: bold (**...**) checked before italic (*...*) so the italic regex
// doesn't claim the leading `*` of a bold pair.
function inlineToJsx(text, keyBase) {
  var escaped = escapeHtml(text);
  var tokens = [];
  var cursor = 0;
  var pattern = /(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)|(https?:\/\/[^\s<]+)/g;
  var match;
  while ((match = pattern.exec(escaped)) !== null) {
    if (match.index > cursor) tokens.push(escaped.slice(cursor, match.index));
    var bold = match[1], ital = match[2], italU = match[3], url = match[4];
    if (bold) {
      tokens.push(
        <strong key={keyBase + '-b-' + match.index} style={{ fontWeight: 700, color: P.INK }}>
          {bold.slice(2, -2)}
        </strong>
      );
    } else if (ital) {
      tokens.push(
        <em key={keyBase + '-i-' + match.index} style={{ fontStyle: 'italic', color: P.BR2 }}>
          {ital.slice(1, -1)}
        </em>
      );
    } else if (italU) {
      tokens.push(
        <em key={keyBase + '-iu-' + match.index} style={{ fontStyle: 'italic', color: P.BR2 }}>
          {italU.slice(1, -1)}
        </em>
      );
    } else if (url) {
      tokens.push(
        <a key={keyBase + '-a-' + match.index}
           href={url} target="_blank" rel="noopener noreferrer"
           style={{
             color: P.BR,
             textDecoration: 'underline',
             textUnderlineOffset: '0.18em',
           }}>{url}</a>
      );
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < escaped.length) tokens.push(escaped.slice(cursor));
  return tokens;
}

export default function MarkdownRender(props) {
  var text = props.markdown || props.text || '';
  if (!text || text.trim() === '') return null;

  // Split into blocks at blank lines
  var blocks = text.split(/\n\s*\n/);
  var elements = [];

  blocks.forEach(function(block, blockIdx) {
    var trimmed = block.trim();
    if (!trimmed) return;

    var lines = trimmed.split('\n');

    // Heading: single-line ## prefix
    if (lines.length === 1 && /^##\s+/.test(lines[0])) {
      var heading = lines[0].replace(/^##\s+/, '');
      elements.push(
        <h3 key={'b' + blockIdx} style={{
          fontFamily: FF,
          fontSize: 'clamp(20px, 2.2vw, 26px)',
          fontStyle: 'italic',
          fontWeight: 500,
          color: P.BR2,
          lineHeight: 1.3,
          margin: 'clamp(28px, 3vw, 36px) 0 clamp(14px, 1.6vw, 18px)',
        }}>{inlineToJsx(heading, 'h-' + blockIdx)}</h3>
      );
      return;
    }

    // List block: every line starts with - or *
    var isList = lines.every(function(l) { return /^[-*]\s+/.test(l); });
    if (isList) {
      elements.push(
        <ul key={'b' + blockIdx} style={{
          listStyle: 'none',
          padding: 0,
          margin: '0 0 clamp(20px, 2.4vw, 28px)',
        }}>
          {lines.map(function(l, i) {
            var item = l.replace(/^[-*]\s+/, '');
            return (
              <li key={'l' + i} style={{
                position: 'relative',
                paddingLeft: 36,
                marginBottom: 16,
                fontFamily: FF,
                fontSize: 'clamp(17px, 1.5vw, 19px)',
                lineHeight: 1.7,
                color: P.TX,
              }}>
                <span style={{
                  position: 'absolute',
                  left: 12,
                  top: '0.7em',
                  width: 9,
                  height: 9,
                  background: P.BR,
                  transform: 'rotate(45deg)',
                }} />
                {inlineToJsx(item, 'l-' + blockIdx + '-' + i)}
              </li>
            );
          })}
        </ul>
      );
      return;
    }

    // Paragraph — soft-break newlines within join as spaces (markdown convention)
    var paragraph = lines.join(' ');
    elements.push(
      <p key={'b' + blockIdx} style={{
        fontFamily: FF,
        fontSize: 'clamp(17px, 1.5vw, 19px)',
        lineHeight: 1.85,
        color: P.TX,
        margin: '0 0 clamp(18px, 2vw, 24px)',
      }}>{inlineToJsx(paragraph, 'p-' + blockIdx)}</p>
    );
  });

  return <React.Fragment>{elements}</React.Fragment>;
}
